/**
 * useDepartmentAggregates
 *
 * The per-department aggregates (gross, units, expenses, operating profit, per-account amounts)
 * that SeasonalityModeView hands back to the page for saving. Captures the EXACT values the
 * view displays. Extracted verbatim from SeasonalityModeView.
 */

import { useMemo } from 'react';
import { MonthlyTotals, createEmptyMonthlyTotals, addMonthlyTotals } from '../../types';
import { DepartmentTemplate } from '../../config/docTemplate';
import { applySeasonality } from '../../utils/seasonalityCalculation';
import { DepartmentAggregates, SeasonalityModeViewProps } from './seasonalityModeTypes';
import { SeasonalityModeData } from './useSeasonalityModeData';

interface UseDepartmentAggregatesArgs extends Pick<SeasonalityModeViewProps,
  'getCalculatedGross' | 'getProjectionValue' | 'getTotalProjectedUnits'> {
  visibleDepartments: DepartmentTemplate[];
  data: SeasonalityModeData;
}

export function useDepartmentAggregates({
  visibleDepartments,
  data,
  getCalculatedGross,
  getProjectionValue,
  getTotalProjectedUnits,
}: UseDepartmentAggregatesArgs): DepartmentAggregates[] {
  const {
    getStoredMonthlyDistribution,
    getVehicleDeptMonthlyGross,
    accountMap2023,
    accountMap2024,
    accountMap2025,
    hasAccountData,
    getDeptGrossSeasonalityWeights,
    getDeptExpenseSeasonalityWeights,
    getDeptUnitSeasonalityWeights,
    getAccountSeasonalityWeights,
    getCategoryMonthlyExpense,
  } = data;

  // Calculate per-department aggregates for saving to backend
  // This captures the EXACT values displayed on the page
  const departmentAggregates = useMemo(() => {
    const aggregates: DepartmentAggregates[] = [];

    visibleDepartments.forEach(dept => {
      const deptGrossWeights = getDeptGrossSeasonalityWeights(dept.name);
      const deptExpenseWeights = getDeptExpenseSeasonalityWeights(dept.name);
      const grossIsZero = deptGrossWeights.every(w => Math.abs(w - 1/12) < 0.0001);
      const deptWeights = grossIsZero ? deptExpenseWeights : deptGrossWeights;

      // Calculate projectedGross (same logic as renderDepartment)
      let projectedGross = 0;
      let incomeAccounts: Record<string, { annual: number; monthly: MonthlyTotals }> | undefined;
      if (dept.hasFandI) {
        const grossCalc = getCalculatedGross(dept.name, 'GROSS');
        const fiCalc = getCalculatedGross(dept.name, 'FI_INCOME');
        projectedGross = grossCalc.total + fiCalc.total;
      } else if (dept.name === 'RENTAL') {
        projectedGross = getProjectionValue(dept.name, 'RENTAL_INCOME') || 0;
      } else if (dept.name === 'OTHERS') {
        const otherIncomeCategory = dept.expenseCategories.find(cat => cat.name === 'OTHER_INCOME');
        if (otherIncomeCategory) {
          const totalProjectedUnits = getTotalProjectedUnits('NEW_VEHICLE') + getTotalProjectedUnits('USED_VEHICLE');
          otherIncomeCategory.accounts.forEach(acc => {
            if (hasAccountData(acc.code)) {
              if (acc.code === '850') {
                const docFeePerUnit = getProjectionValue(dept.name, 'INCOME_850_PER_UNIT') || 0;
                projectedGross += docFeePerUnit * totalProjectedUnits;
              } else {
                projectedGross += getProjectionValue(dept.name, `INCOME_${acc.code}`) || 0;
              }
            }
          });
        }
        // Collect per-account income totals for consolidated aggregation
        // This creates INCOME_850 (total) alongside the existing INCOME_850_PER_UNIT (per-unit)
        if (otherIncomeCategory) {
          const totalProjectedUnitsForIncome = getTotalProjectedUnits('NEW_VEHICLE') + getTotalProjectedUnits('USED_VEHICLE');
          incomeAccounts = {};
          otherIncomeCategory.accounts.forEach(acc => {
            if (hasAccountData(acc.code)) {
              let accValue = 0;
              if (acc.code === '850') {
                const docFeePerUnit = getProjectionValue(dept.name, 'INCOME_850_PER_UNIT') || 0;
                accValue = docFeePerUnit * totalProjectedUnitsForIncome;
              } else {
                accValue = getProjectionValue(dept.name, `INCOME_${acc.code}`) || 0;
              }
              // Zero is emitted too, so a revision to 0 replaces the old INCOME_xxx total
              const accWeights = getAccountSeasonalityWeights(acc.code);
              const useAccWeights = accWeights.every(w => Math.abs(w - 1/12) < 0.0001) ? deptWeights : accWeights;
              incomeAccounts![acc.code] = {
                annual: accValue,
                monthly: accValue !== 0 ? applySeasonality(accValue, useAccWeights) : createEmptyMonthlyTotals(),
              };
            }
          });
        }
      } else {
        projectedGross = getProjectionValue(dept.name, 'GROSS') || 0;
      }

      // Calculate expenses (same logic as renderDepartment)
      let totalExpenses = 0;
      let monthlyExpenses = createEmptyMonthlyTotals();
      const expenseAccounts: Record<string, { annual: number; monthly: MonthlyTotals }> = {};

      dept.expenseCategories
        .filter(cat => !['RENTAL_INCOME', 'OTHER_INCOME'].includes(cat.name))
        .forEach(cat => {
          const categoryData = getCategoryMonthlyExpense(cat, dept.name, projectedGross, deptWeights);
          totalExpenses += categoryData.annual;
          monthlyExpenses = addMonthlyTotals(monthlyExpenses, categoryData.monthly);

          // Collect individual expense account amounts (for consolidated aggregation)
          categoryData.accountMonthly.forEach((monthly, accCode) => {
            // Calculate annual from monthly totals
            let annual = 0;
            for (let m = 1; m <= 12; m++) {
              annual += monthly[`month${m}` as keyof MonthlyTotals];
            }
            if (annual !== 0) {
              expenseAccounts[accCode] = { annual, monthly };
            }
          });
        });

      // Get monthly gross for profit calculation. These aggregates become the save payload, so
      // they use the seasonality BASE spread: the backend re-applies any Executive monthly
      // adjustment on top (sending the adjusted spread back would double it). The screen and
      // the export read the adjusted spread separately.
      let monthlyGross: MonthlyTotals;
      if (dept.hasFandI) {
        monthlyGross = getVehicleDeptMonthlyGross(dept.name, projectedGross, deptGrossWeights, 'base');
      } else if (dept.name === 'RENTAL') {
        monthlyGross = getStoredMonthlyDistribution(dept.name, 'RENTAL_INCOME', projectedGross, deptGrossWeights, 'base');
      } else {
        monthlyGross = getStoredMonthlyDistribution(dept.name, 'GROSS', projectedGross, deptGrossWeights, 'base');
      }

      // Calculate operating profit
      const operatingProfit = projectedGross - totalExpenses;
      const monthlyProfit = createEmptyMonthlyTotals();
      for (let m = 1; m <= 12; m++) {
        const key = `month${m}` as keyof MonthlyTotals;
        monthlyProfit[key] = monthlyGross[key] - monthlyExpenses[key];
      }

      // Calculate units for vehicle departments (same logic as display)
      let units: number | undefined;
      let unitsMonthly: MonthlyTotals | undefined;
      if (dept.hasFandI) {
        units = getTotalProjectedUnits(dept.name);
        // Always emit a distribution (zeros for 0 units) so a revision to 0 is
        // saved instead of leaving the previous UNITS aggregate in place
        // Base UNITS spread (see monthlyGross above); the screen shows the adjusted one
        unitsMonthly = units > 0
          ? getStoredMonthlyDistribution(dept.name, 'UNITS', units, getDeptUnitSeasonalityWeights(dept.name), 'base')
          : createEmptyMonthlyTotals();
      }

      aggregates.push({
        department: dept.name,
        gross: projectedGross,
        grossMonthly: monthlyGross,
        units,
        unitsMonthly,
        totalExpenses,
        totalExpensesMonthly: monthlyExpenses,
        operatingProfit,
        operatingProfitMonthly: monthlyProfit,
        expenseAccounts: Object.keys(expenseAccounts).length > 0 ? expenseAccounts : undefined,
        incomeAccounts: incomeAccounts && Object.keys(incomeAccounts).length > 0 ? incomeAccounts : undefined,
      });
    });

    return aggregates;
  }, [getCalculatedGross, getProjectionValue, getTotalProjectedUnits, getCategoryMonthlyExpense, getVehicleDeptMonthlyGross, getStoredMonthlyDistribution, getDeptGrossSeasonalityWeights, getDeptExpenseSeasonalityWeights, getDeptUnitSeasonalityWeights, accountMap2023, accountMap2024, accountMap2025, hasAccountData]);

  return departmentAggregates;
}
